import React, {useState, useEffect} from 'react';
import View from './view';
import {EsiaRegCardForm, FormProps, KidSubform} from './model';
import {destroy, getFormValues, initialize, InjectedFormProps, reduxForm, reset} from 'redux-form';
import {Forms} from '../../../../../shared/models/forms';
import {useDispatch, useSelector} from 'react-redux';
import {esiaRegistrationInfoSelector} from '../../../../../redux/selectors/login';
import {getApiRoute, validationPipe} from '../../../../../shared/pipes';
import {validationItems} from './constants';
import {validationItems as kidValidationItems} from './kidSubform/constants';
import {RegisterClientCabinetRequestPayload} from '../../../../../shared/models/api/registerClientCabinetRequestPayload';
import {loginSlice} from '../../../../../redux/reducers/login';
import {mapEsiaRegInfoToFormValues, mapFormValuesToEsiaRegData, mapFormValuesToEsiaRegInfo} from '../formMapper';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {store} from '../../../../../redux';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {hasOnlyUniqueVals} from '../../../../../shared/utils/hasOnlyUniqueVals';
import {ServerValidationError} from '../../../../../shared/models/serverValidationError';
import {httpService, HttpWrapperOptions} from '../../../../../shared/httpWrapper';
import {apiUrls} from '../../../../../shared/constants/urls';

const getFormErrors = (formData: Omit<RegisterClientCabinetRequestPayload, 'accessToken'>) => {
  return validationPipe(formData, validationItems);
};

const getKidFormErrors = (formData: Omit<RegisterClientCabinetRequestPayload, 'accessToken'>) => {
  return validationPipe(formData, kidValidationItems);
};

const _Form = ({handleSubmit, onEsiaSubmit}: FormProps & InjectedFormProps<EsiaRegCardForm, FormProps>) => {
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState(null);
  const onAcceptedToggle = () => setIsAccepted((prev) => !prev);

  const {parent, setConfirmedKids, checkedKids, addData} = useRegCardState();

  const regInfo = useSelector(esiaRegistrationInfoSelector);

  //@ts-ignore
  const appReduxForms = useSelector((state) => state.form);
  useEffect(() => {
    return Object.keys(appReduxForms).forEach((form) => dispatch(destroy(form)));
  }, []);

  const formValues = useSelector((state) => {
    return getFormValues(Forms.esiaRegCardForm)(state) as EsiaRegCardForm;
  });

  const formsMobileNumbers: string[] = [];
  const formsEmails: string[] = [];
  const getKidsFormsErrors = () => {
    return checkedKids
      .map((kid, index, _arr) => {
        const kidFormValues = getFormValues(`${Forms.kidSubform}-${index}`)(store.getState()) as KidSubform;
        const submissionData = mapFormValuesToEsiaRegData(kidFormValues);
        if (submissionData.mobilePhone) formsMobileNumbers.push(submissionData.mobilePhone.slice(1));
        if (submissionData.email) formsEmails.push(submissionData.email);
        return getKidFormErrors(submissionData);
      })
      .filter((v) => v != null);
  };

  useEffect(() => {
    if (parent) {
      dispatch(initialize(Forms.esiaRegCardForm, mapEsiaRegInfoToFormValues(parent)));
    }
  }, [parent]);

  const onLocalSubmit = (e?: any) => {
    e?.preventDefault();
    handleSubmit(async () => {
      try {
        if (!regInfo) return;

        const submissionData = mapFormValuesToEsiaRegData(formValues, regInfo.token);

        if (submissionData.mobilePhone && parent?.checked) formsMobileNumbers.push(submissionData.mobilePhone.slice(1));
        if (submissionData.email && parent?.checked) formsEmails.push(submissionData.email);

        const errors = getFormErrors(submissionData);

        if (errors) {
          console.error('errors', errors);
          setSubmissionErrors(errors);
          if (errors) {
            dispatch(
              addNotification({
                duration: 6000,
                message: Object.values(errors)
                  .map((v: any) => v.error)
                  .join(', '),
                type: SnackbarNotificationType.Error,
              }),
            );
          }
          return;
        }

        const kidsErrors = getKidsFormsErrors();
        if (kidsErrors?.length) {
          console.error('kidsErrors', kidsErrors);
          if (kidsErrors?.length) {
            dispatch(
              addNotification({
                duration: 6000,
                message: Object.values(kidsErrors[0])
                  .map((v: any) => v.error)
                  .join(', '),
                type: SnackbarNotificationType.Error,
              }),
            );
          }
          return;
        }

        const isEmailsUnique = hasOnlyUniqueVals(formsEmails);
        if (!isEmailsUnique) {
          dispatch(
            addNotification({
              duration: 6000,
              message: 'Эл. почта должна быть уникальныма',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const isMobileNumbersUnique = hasOnlyUniqueVals(formsMobileNumbers);
        if (!isMobileNumbersUnique) {
          dispatch(
            addNotification({
              duration: 6000,
              message: 'Номера мобильных телефонов должны быть уникальными',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const response = await httpService.post<{duplicateEmails: string[]; duplicateMobilePhones: string[]}>(
          getApiRoute(apiUrls.checkContactsUniqueness),
          {
            emails: formsEmails,
            mobilePhones: formsMobileNumbers,
          },
          new HttpWrapperOptions().Errors(true).ThrowException(true).Wait(true),
        );
        if (response.duplicateEmails.length) {
          dispatch(
            addNotification({
              duration: 6000,
              message:
                'Данные email уже используются: ' +
                response.duplicateEmails.join(', ') +
                '. Измените email или обратитесь в тех. поддержку',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }
        if (response.duplicateMobilePhones.length) {
          dispatch(
            addNotification({
              duration: 6000,
              message:
                'Данные телефоны уже используются: ' +
                response.duplicateMobilePhones.join(', ') +
                '. Измените или обратитесь в тех. поддержку',
              type: SnackbarNotificationType.Error,
            }),
          );
          return;
        }

        const updatedRegInfo = mapFormValuesToEsiaRegInfo(formValues, regInfo.token);
        const formDataKids = checkedKids.map((kid, kidIndex) => {
          const formData = getFormValues(`${Forms.kidSubform}-${kidIndex}`)(store.getState()) as KidSubform;
          return formData;
        });
        setConfirmedKids(formDataKids);
        dispatch(loginSlice.actions.setEsiaRegistrationInfo(updatedRegInfo));
        onEsiaSubmit();
      } catch (ex) {
        const error = ex as ServerValidationError;
        let errorText = error.data?.[0].message || error.message || 'Ошибка. Обратитесь в тех поддержку';
        dispatch(
          addNotification({
            duration: 6000,
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      }
    })();
  };

  return (
    <View
      formValues={formValues}
      isAccepted={isAccepted}
      onAcceptedToggle={onAcceptedToggle}
      loading={false}
      submissionErrors={submissionErrors}
      setSubmissionErrors={setSubmissionErrors}
      onSubmit={onLocalSubmit}
      submitting={submitting}
      // regInfo={regInfo}
    />
  );
};

export const EsiaRegCardFormScreen = reduxForm<EsiaRegCardForm, FormProps>({
  form: Forms.esiaRegCardForm,
  destroyOnUnmount: false,
  keepValues: true,
})(_Form);
